/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import PropTypes from 'prop-types'

import Button from '../Button'
import DeleteModal from '../DeleteModal'
import DropdownButton from '../DropdownButton'
import Modal from '../Modal'
import Search from '../Search'
import TextLink from '../TextLink'
import { useToggle } from '../utils/hooks'

const FilterGroups = ({ filterGroupsConfig, filters, setFilters }) => {
  const {
    tableKey,
    useGet,
    useCreate,
    useUpdate,
    useDelete
  } = filterGroupsConfig

  const { data: filterGroups = [], isFetching } = useGet(tableKey, { skip: !tableKey })

  const [createGroup, { isLoading: isCreateLoading }] = useCreate()
  const [overwriteGroup, { isLoading: isOverwriteLoading }] = useUpdate()
  const [deleteGroup, { isLoading: isDeleteLoading }] = useDelete()

  const [searchValue, setSearchValue] = useState('')
  const [saveFilterGroupType, setSaveFilterGroupType] = useState(null) // null, 'Save New', 'Save As'
  const [saveSearchValue, setSaveSearchValue] = useState('')

  const [updatingGroupUUID, setUpdatingGroupUUID] = useState(null)

  const [selectedGroup, setSelectedGroup] = useState({})
  const [isOverwriteOpen, toggleOverwrite] = useToggle()

  const [groupToDelete, setGroupToDelete] = useState({})
  const [isDeleteOpen, toggleDelete] = useToggle()

  const [showApplyFiltersMenu, toggleApplyFiltersMenu] = useToggle()
  const [showSaveGroupMenu, toggleSaveGroupMenu] = useToggle()

  const FilterGroup = ({ group, isLastItem }) => {
    const [updateGroup, { isLoading: isUpdateLoading }] = useUpdate()

    const [updatingGroupName, setUpdatingGroupName] = useState(group.name)

    return (
      <div className={`d-flex align-items-center justify-content-between py-1 ${!isLastItem ? 'border-bottom' : ''}`}>
        {updatingGroupUUID === group.uuid
          ? (
            <>
              <input
                id='updatingGroupName'
                style={{ width: '150px', height: '2.5rem', fontSize: '.875rem' }}
                className='form-control me-2'
                type='text'
                aria-label='updatingGroupName'
                name='updatingGroupName'
                placeholder='Name...'
                onChange={(e) => setUpdatingGroupName(e.target.value)}
                value={updatingGroupName}
              />

              <div className='text-nowrap'>
                <Button
                  variant='success'
                  size='sm'
                  className='ms-2'
                  disabled={isUpdateLoading}
                  onClick={() => {
                    const updatePayload = {
                      data: {
                        uuid: group.uuid,
                        name: updatingGroupName
                      },
                      cb: () => {
                        toggleApplyFiltersMenu(false)
                        setUpdatingGroupUUID(null)
                      }
                    }
                    updateGroup(updatePayload)
                  }}
                >
                  <span className={isUpdateLoading ? 'fas fa-spinner fa-spin' : 'fas fa-check'} />
                </Button>
                <Button
                  ghost
                  size='sm'
                  className='text-danger ms-1'
                  disabled={isUpdateLoading}
                  onClick={() => setUpdatingGroupUUID(null)}
                >
                  <span className='fas fa-times' />
                </Button>
              </div>
            </>
          )
          : (
            <>
              <TextLink
                variant='black'
                onClick={() => {
                  setFilters(group.filters)
                  toggleApplyFiltersMenu(false)
                }}
              >
                <div className='fw-normal text-start text-break' style={{ width: '150px' }}>{group.name}</div>
              </TextLink>

              <div className='text-nowrap'>
                <Button
                  ghost
                  size='sm'
                  className='text-muted ms-2'
                  disabled={!!updatingGroupUUID}
                  onClick={() => {
                    setUpdatingGroupUUID(group.uuid)
                  }}
                >
                  <span className='fas fa-pen' />
                </Button>
                <Button
                  ghost
                  size='sm'
                  className='text-muted'
                  disabled={!!updatingGroupUUID}
                  onClick={() => {
                    setGroupToDelete(group)
                    toggleDelete()
                  }}
                >
                  <span className='fas fa-times' />
                </Button>
              </div>
            </>
          )
        }

      </div>
    )
  }

  const SaveFilterGroupDropdown = () => {
    const [newGroupName, setNewGroupName] = useState('')

    if (!saveFilterGroupType) {
      return (
        <>
          <a className='dropdown-item pointer' onClick={() => setSaveFilterGroupType('Save New')}>
            Save New
          </a>
          <a className='dropdown-item pointer' onClick={() => setSaveFilterGroupType('Save As')}>
            Save As...
          </a>
        </>
      )
    }

    if (saveFilterGroupType === 'Save New') {
      return (
        <div className='form-group col-12'>
          <div className='d-flex'>
            <input
              id='newGroupName'
              style={{ minWidth: '150px' }}
              className='form-control me-2'
              type='text'
              aria-label='newGroupName'
              name='newGroupName'
              placeholder='Name...'
              onChange={(e) => setNewGroupName(e.target.value)}
              value={newGroupName}
            />
            <Button
              variant='success'
              size='sm'
              disabled={newGroupName.length === 0 || isCreateLoading}
              className='me-2'
              onClick={() => {
                const createPayload = {
                  data: {
                    name: newGroupName,
                    table_key: tableKey,
                    filters: JSON.stringify(filters)
                  },
                  cb: () => {
                    setNewGroupName('')
                    toggleSaveGroupMenu(false)
                  }
                }
                createGroup(createPayload)
              }}
            >
              <span className={isCreateLoading ? 'fas fa-spinner fa-spin px-1' : 'fas fa-check px-1'} />
            </Button>
            <Button
              ghost
              className='text-muted'
              size='sm'
              onClick={() => {
                setNewGroupName('')
                toggleSaveGroupMenu(false)
              }}
            >
              <span className='fas fa-times px-1' />
            </Button>
          </div>
        </div>
      )
    }

    if (saveFilterGroupType === 'Save As') {
      const saveAsFilterGroupsFilter = filterGroups.filter((d) => d.name.toLowerCase().includes(saveSearchValue.toLowerCase()))
      return (
        <>
          {saveAsFilterGroupsFilter.map((d, i, arr) => (
            <div key={`overwrite-${d.uuid}`} className={`d-flex justify-content-between align-items-center py-2 ${i !== arr.length - 1 ? 'border-bottom' : ''}`}>
              <span className='text-break me-2' style={{ width: '150px' }}>
                {d.name}
              </span>
              <Button
                variant='success'
                size='sm'
                onClick={() => {
                  setSelectedGroup(d)
                  toggleOverwrite(true)
                }}
              >
                <span className='fas fa-floppy-disk' />
              </Button>
            </div>
          ))}
        </>
      )
    }
  }

  const options = filterGroups.length > 0
    ? filterGroups.filter(d => d.name.toLowerCase().includes(searchValue.toLowerCase()))
      .map((d, i, arr) => ({
        text: true,
        content: <FilterGroup key={d.uuid} group={d} isLastItem={i === arr.length - 1} />
      }))
    : [{
      text: true,
      content: <div className='px-3 py-1'>No Groups</div>
    }]

  return (
    <>
      <DropdownButton
        show={showApplyFiltersMenu}
        toggle={(newShow) => {
          setUpdatingGroupUUID(null)
          toggleApplyFiltersMenu(newShow)
        }}
        ghost
        // drop='end'
        className='me-2'
        disabled={isFetching}
        stickyItems={
          filterGroups.length > 0 && <div className='filter-group-item'>
            <Search
              search={searchValue}
              setSearchTerm={(e) => setSearchValue(e.target.value)}
              size='sm'
            />
          </div>
        }
        options={options}
      >
        <span className={isFetching ? 'fas fa-spinner fa-spin' : 'fa-solid fa-filters'} /> Apply Filter Group
      </DropdownButton>

      {filters.length > 0 && (
        <DropdownButton
          show={showSaveGroupMenu}
          toggle={() => {
            toggleSaveGroupMenu()
            setSaveFilterGroupType(null)
          }}
          ghost
          disabled={isFetching}
          stickyItems={
            saveFilterGroupType === 'Save As' &&
            <div className='filter-group-item'>
              <Search
                search={saveSearchValue}
                setSearchTerm={(e) => setSaveSearchValue(e.target.value)}
                size='sm'
              />
            </div>
          }
          options={[
            {
              text: true,
              content: <SaveFilterGroupDropdown />
            }
          ]}
        >
          <span><span className='fas fa-save' />{` Save (${filters.length}) Filters as Group`}</span>
        </DropdownButton>
      )}

      {/* Overwrite Filter Group Modal */}
      <Modal
        isOpen={isOverwriteOpen}
        onHide={toggleOverwrite}
        size='sm'
        variant='success'
        title='Are you sure you want to overwrite this filter group?'
        icon='floppy-disk'
        loading={isOverwriteLoading}
        cancelButton
        ctaButton
        ctaText='Save'
        ctaFunction={() => {
          const overwritePayload = {
            data: {
              uuid: selectedGroup.uuid,
              name: selectedGroup.name,
              filters: JSON.stringify(filters)
            },
            cb: () => {
              toggleOverwrite(false)
              toggleApplyFiltersMenu(false)
            }
          }
          overwriteGroup(overwritePayload)
        }}
      >
        <span>
          Saving this filter group as <span className='fw-bold'>{selectedGroup.name}</span> will overwrite the current filters saved under that name. Are you sure?
        </span>
      </Modal>

      {/* Delete Filter Group Modal */}
      <DeleteModal
        isOpen={isDeleteOpen}
        onHide={toggleDelete}
        title='Delete Filter Group'
        item={groupToDelete.name}
        loading={isDeleteLoading}
        ctaFunction={() => {
          const deletePayload = {
            filter_group_uuid: groupToDelete.uuid,
            cb: toggleDelete
          }
          deleteGroup(deletePayload)
        }}
      />
    </>
  )
}

FilterGroups.propTypes = {
  filterGroupsConfig: PropTypes.object,
  filters: PropTypes.array,
  setFilters: PropTypes.func
}

export default FilterGroups
